/**
 * Talking to the law service.
 *
 * Every path is written as it is once deployed, because FastAPI serves this
 * build from the same origin. In development Vite forwards `/v1` across, so
 * nothing here needs to know there are two processes.
 *
 * Reading needs no key, so there is no auth here at all.
 */

export type Law = {
  bill_id: string;
  congress: number;
  bill_type: string;
  number: number;
  title: string;
  law_number: string | null;
  policy_area: string | null;
  latest_action_date: string | null;
  /** From the summariser. Absent for a law that has not been summarised. */
  plain_title: string | null;
  key_points: string[] | null;
  who_it_affects: string | null;
  confidence: string | null;
  grounding: string | null;
  model: string | null;
  word_count: number | null;
  section_count: number | null;
};

export type Answer = {
  bill_id: string;
  question: string;
  answer: string;
  /** False when the retrieved sections did not settle it. The model is
   *  asked to say so rather than reach for what it remembers. */
  supported: boolean;
  cited: string[];
  model: string;
  sections_used: {
    number: string | null;
    heading: string | null;
    similarity: number;
    excerpt: string;
  }[];
  note: string;
};

export type LawDetail = Law & {
  official_title: string | null;
  summary: string | null;
  sections: { ordinal: number; number: string | null; heading: string | null; word_count: number }[];
  /** Chunks with an embedding, so: how many passages `ask` could retrieve.
   *  Zero means the question box should not be offered — the serving corpus
   *  can be built without vectors, and answering from no passages is worse
   *  than not answering. */
  indexed: number;
  generated_at: string | null;
};

export type PartyTally = {
  yea: number;
  nay: number;
  present: number;
  not_voting: number;
  voted: number;
  /** Null, not zero, when every member of that party was absent. */
  share_yea: number | null;
};

export type ChamberVote = {
  vote_id: string;
  question: string;
  result: string;
  date: string;
  yea: number;
  nay: number;
  present: number;
  not_voting: number;
  parties: Record<string, PartyTally>;
  party_spread: number | null;
};

export type LawVotes = {
  bill_id: string;
  rollcalls: number;
  /** False for the 408 laws that passed on a voice vote. */
  recorded: boolean;
  chambers: Partial<Record<"house" | "senate", ChamberVote>>;
};

export type Landscape = {
  laws: number;
  recorded: number;
  voice: number;
  scored: number;
  buckets: { key: string; label: string; floor: number; laws: number }[];
  support: Record<string, { share: number; votes: number }>;
  contested: {
    bill_id: string;
    title: string;
    plain_title: string | null;
    law_number: string | null;
    chamber: string;
    date: string;
    spread: number;
    parties: Record<string, number>;
  }[];
};

/** One law as the vote landscape sees it: how far apart the parties were,
 *  in whichever chamber they were furthest apart. */
export type BandedLaw = {
  bill_id: string;
  title: string;
  plain_title: string | null;
  law_number: string | null;
  chamber: string;
  date: string;
  /** 0 when both parties voted the same way, 1 when they were opposite. */
  spread: number;
  parties: Record<string, number>;
};

export type BandPage = {
  band: { key: string; label: string; floor: number };
  total: number;
  page: number;
  pages: number;
  per_page: number;
  laws: BandedLaw[];
};

/** One rung of the legislative process, and what reaching it has been
 *  worth. `of` travels with `share` on purpose: a rate is not reportable
 *  without the count it was drawn from. `share` is null for a rung no bill
 *  in the finished Congresses ever reached -- which is not the same as a
 *  rung nothing ever passed from. */
export type Rung = {
  key: string;
  label: string;
  rank: number;
  blurb: string;
  became_law: number;
  of: number;
  share: number | null;
};

/** Which rung a particular bill is standing on. The same four keys as
 *  `Rung`, without the historical counts — those belong to the rung and not
 *  to the bill. */
export type Stage = { key: string; label: string; rank: number; blurb: string };

/** How often a bill standing where this one stands went on to become law.
 *  `of` travels with `share` for the same reason it does on `Rung`: the rate
 *  is not reportable without the count behind it. */
export type Odds = { became_law: number; of: number; share: number | null };

export type MovingBill = {
  bill_id: string;
  congress: number;
  bill_type: string;
  number: number;
  display_name: string | null;
  title: string;
  policy_area: string | null;
  introduced: string | null;
  sponsor_name: string | null;
  sponsor_party: string | null;
  sponsor_state: string | null;
  cosponsor_count: number | null;
  latest_action: string | null;
  latest_action_date: string | null;
  summarised: boolean;
  /** Named, so a card can say "passed the House" and not "passed one". */
  chambers: string[];
  stage: Stage;
  odds: Odds | null;
};

export type MovingBills = {
  congress: number;
  total: number;
  page: number;
  pages: number;
  per_page: number;
  ladder: Rung[];
  bills: MovingBill[];
};

/** One name on a bill, as the chambers publish it.
 *
 *  `full_name` is their own formatting — "Sen. Blackburn, Marsha [R-TN]" —
 *  and already carries the party and the state inside the string. It is
 *  shown as filed rather than recomposed, so what is on the page is what is
 *  on the record. */
export type BillPerson = {
  bill_id: string;
  bioguide: string | null;
  role: string;
  full_name: string | null;
  party: string | null;
  state: string | null;
  district: number | null;
  signed: string | null;
  original: boolean;
  withdrawn: string | null;
};

/** One roll call on a bill still in progress.
 *
 *  Every recorded vote, not only the ones on passage: cloture, motions to
 *  suspend the rules and motions to table are all in here. So a count of
 *  these is a count of times somebody asked for a tally, which is not the
 *  same as the number of times the bill was voted on. */
export type BillVote = {
  vote_id: string;
  chamber: string;
  roll: number | null;
  vote_date: string | null;
  question: string | null;
  result: string | null;
  yeas: number;
  nays: number;
  present: number;
  not_voting: number;
};

/** A plain-English account of a bill written by a language model.
 *
 *  Nothing in it came from Congress, which is why it is a separate field
 *  from `summary` and never merged into it. `grounding` is what the model
 *  was given: an explainer written from a title alone is a far weaker claim
 *  than one written from the official summary, and the page has to be able
 *  to say which it is holding.
 *
 *  Every field is optional because the shape is still settling on the
 *  server. A missing one is rendered as a missing one; none of them is
 *  guessed at. */
export type Explainer = {
  /** A headline saying what the bill does. Never used as the page's own
   *  title: the heading of a bill page is the title Congress gave it, and a
   *  generated line in that position would be the one place on the site
   *  where the most prominent words are not the record's. */
  plain_title?: string | null;
  key_points?: string[] | null;
  who_it_affects?: string | null;
  /** Prose forms, for a server that sends one instead of points. */
  text?: string | null;
  points?: string[] | null;
  /** "title only", "summary only", "headings", "partial text", "full text".
   *  For a bill only the first two happen — the corpus holds filed text for
   *  enacted laws — and they are a long way apart. */
  grounding?: string | null;
  confidence?: string | null;
  model?: string | null;
  generated?: boolean;
};

/**
 * One bill that has not become law.
 *
 * The first block of fields is disclosure: it is what the chambers publish
 * about the bill, read back. `stage`, `odds` and `ladder` are inference and
 * arithmetic over the finished Congresses, and `explainer` is written by a
 * model. Those four are optional because the server is still growing them,
 * and the page must draw without any of them rather than break.
 */
export type BillDetail = {
  bill_id: string;
  congress: number;
  bill_type: string;
  number: number;
  title: string;
  display_name: string | null;
  origin_chamber: string | null;
  introduced: string | null;
  policy_area: string | null;
  sponsor: BillPerson | null;
  /** Congress's own summary, written by the Congressional Research Service.
   *  Official text, and the only prose on the page that is. */
  summary: string | null;
  summary_date: string | null;
  /** Which point in the bill's life the summary describes. A summary of the
   *  introduced text is not a summary of what passed the Senate. */
  summary_stage: string | null;
  law_number: string | null;
  latest_action: string | null;
  latest_action_date: string | null;
  action_count: number | null;
  cosponsor_count: number | null;
  url: string | null;
  became_law: boolean;
  subjects: string[];
  /** Committee system codes — "ssju00" — not names. */
  committees: string[];
  cosponsors: BillPerson[];
  votes: BillVote[];
  stage?: Stage | null;
  odds?: Odds | null;
  ladder?: Rung[] | null;
  /** Named, so a page can say "passed the House" and not "passed one". */
  chambers?: string[] | null;
  explainer?: Explainer | null;
};

/** How often someone voted with the rest of their own party.
 *
 *  `share` is null rather than zero when `votable` is zero. A member who has
 *  never had a party position to hold is not a rebel, and 0.0 would sort
 *  them among the rebels. `votable` excludes rolls where the rest of their
 *  party split evenly, since there was no line there to cross. */
export type WithParty = {
  votable: number;
  agreed: number;
  share: number | null;
};

export type Member = {
  bioguide: string;
  full_name: string | null;
  party: string | null;
  state: string | null;
  chamber: string | null;
  votes: number;
  yea: number;
  nay: number;
  present: number;
  missed: number;
  sponsored: number;
  laws: number;
  with_party: WithParty;
  money: MemberCardMoney;
};

/** The money figures a directory row carries, so a card can show the number
 *  it was sorted by. `raised` and `pac_share` are null for a member with no
 *  campaign filing — which is not the same as raising nothing, and very much
 *  not the same as taking no PAC money. */
export type MemberCardMoney = {
  lobbied: number;
  raised: number | null;
  pac_share: number | null;
};

/** The orderings the directory offers. `independent`, `loyal` and `absent`
 *  are rates and rank only members with enough record to place; everyone
 *  else goes last rather than being dropped. */
export type MemberSort =
  | "name"
  | "independent"
  | "loyal"
  | "votes"
  | "absent"
  | "sponsored"
  | "laws"
  | "lobbied"
  | "raised"
  | "pac";

export type MemberPage = {
  total: number;
  members: Member[];
};

/** One lobbying firm's giving to one member. */
export type Donor = {
  registrant: string;
  amount: number;
  gifts: number;
  latest: string | null;
  /** The id its page is keyed on, where the name resolves to exactly one
   *  registrant. A thousand names here are used at more than one, and a
   *  merged link would show a total neither organisation gave. */
  registrant_id: number | null;
};

/** What a member's committee raised in one FEC cycle. Senate cycles run six
 *  years, so `cycle` is when it ended rather than a single year. */
export type Cycle = {
  cycle: number;
  candidate_id: string;
  receipts: number;
  individual: number;
  pac: number;
  party: number;
  from_self: number;
  disbursements: number;
  cash_on_hand: number;
};

/** How much of the lobbyist record ties to a named member. Every total is a
 *  floor, and this is the size of what is missing from it. */
export type LobbyCoverage = {
  total: number;
  matched: number;
  unmatched: number;
  dollars: number;
  dollars_matched: number;
  first_year: number | null;
  last_year: number | null;
};

/** The three money records, which are not comparable and are not summed.
 *  `lobbyist` names who gave and is small; `campaign` is the whole of the
 *  fundraising and cannot name a lobbyist; `party_committees` is a third
 *  thing again and the one that needs its label read, because a member's
 *  largest entry there is routinely the other party's committee spending
 *  against them. */
export type MemberMoney = {
  bioguide: string;
  full_name: string | null;
  lobbyist: {
    total: number;
    /** Events held in their honour. Not money the member received, so it is
     *  reported apart from `total` rather than added to it. */
    honorary: number;
    gifts: number;
    donors_total: number;
    donors: Donor[];
    by_confidence: Record<string, number>;
    /** `total` split by who filed it. Two thirds of these reports are
     *  individual lobbyists disclosing their own money with an employer
     *  named on the form, so a list of organisations is not a list of
     *  givers. See `FirmDetail.by_filer`. */
    by_filer: Record<string, { amount: number; gifts: number }>;
  };
  campaign: Cycle[];
  /** What the DSCC, NRSC, DCCC, NRCC and the state parties spent on this
   *  member, by category. Not folded into `campaign`, because most of it is
   *  not money the member's campaign ever held. */
  party_committees: MemberCommitteeMoney[];
  coverage: LobbyCoverage;
};

/**
 * One person who declared a government post naming this member, and what
 * that same person separately reported giving them.
 *
 * The two halves come off two forms that never refer to each other, and the
 * type cannot enforce that — the page has to. `position` is sworn on a
 * quarterly LD-2 under penalty of perjury; `to_them` is certified by the
 * same person on their own semi-annual LD-203s and may predate the post.
 *
 * `position` is the shortest of the filed wordings, which on about one row
 * in a hundred and thirty is the member's name with no job title attached to
 * it. Rendered without a label in front of it, that row says the lobbyist is
 * the member.
 *
 * `declarations` is how many distinct wordings by distinct registrants stand
 * behind the row rather than how many filings — the same declaration is
 * refiled every quarter it stays true. `registrant` is the one that filed
 * the wording shown, and not necessarily where the person works now.
 */
export type DoorPerson = {
  lobbyist_id: number | null;
  name: string;
  position: string;
  bioguide: string;
  matched_how: string | null;
  registrant: string;
  registrant_id: number | null;
  first_year: number | null;
  last_year: number | null;
  declarations: number;
  firms: number;
  full_name: string | null;
  party: string | null;
  state: string | null;
  chamber: string | null;
  /** What this person disclosed giving this member, across the whole record.
   *  Keyed on their name, because a contribution carries no identifier for
   *  whoever wrote it. */
  to_them: number;
  gifts_to_them: number;
};

/** `total` is of the member and not of the page, so a section showing the
 *  first of twenty-eight can say so. Empty for most of Congress: 534 members
 *  have a declaration pointing at them and the rest have none. */
export type MemberDoor = {
  bioguide: string;
  full_name: string | null;
  people: DoorPerson[];
  total: number;
};

/** One roll this member was recorded on. Most belong to no bill — procedural
 *  motions, quorum calls — and those still show them turning up, so `title`
 *  and `bill_id` are null far more often than not. */
export type MemberVote = {
  vote_id: string;
  position: string;
  date: string | null;
  chamber: string | null;
  roll: number | null;
  question: string | null;
  result: string | null;
  bill_id: string | null;
  title: string | null;
  display_name: string | null;
  policy_area: string | null;
  /** Null unless the bill was enacted. Only enacted bills have a page, and
   *  most rolls are on ones that never got there, so this decides whether
   *  the row is a link or plain text. */
  law_number: string | null;
};

export type MemberDetail = {
  bioguide: string;
  record: Member & { cosponsored: number; laws: number };
  sponsored: { bill_id: string; title: string; law_number: string | null }[];
  votes: MemberVote[];
};

/** How often a lobbying report names a bill by number at all.
 *
 *  Carried with every answer about the money beside a vote, because it is
 *  what an empty row means. About one report in five names a bill; the rest
 *  describe their subject in words that name none, so nothing beside a roll
 *  says nobody filed that bill's number — never that nobody lobbied it.
 *
 *  `share` is null on a corpus with no reports read yet, which is not the
 *  same as filers never naming one. */
export type BillLobbyingCoverage = {
  activities: number;
  named: number;
  bills: number;
  registrants: number;
  share: number | null;
};

/** Money beside one roll, from two filings that never reference each other.
 *
 *  The organisations counted here disclosed lobbying on that roll's bill, on
 *  a quarterly LD-2, and disclosed giving this member money, on a
 *  semi-annual LD-203. Nothing in either form connects them and no filing
 *  says money was given for a bill, so `amount` is everything they have
 *  disclosed giving this member — set beside a roll both filings touch.
 *
 *  `before` and `after` split it at the date of the roll. Money disclosed
 *  after a vote cannot have influenced it; a cheque dated the day of the
 *  roll is counted with `after`, because neither form carries a time.
 *  `unplaced` is money on a roll the chamber published no date for.
 *
 *  There is no field for which way the member voted relative to these
 *  organisations, and there cannot be: an LD-2 discloses the bill and never
 *  whether the filer wanted it passed or killed. */
export type VoteMoneySummary = {
  donors: number;
  gifts: number;
  amount: number;
  before: number;
  after: number;
  unplaced: number;
  gifts_before: number;
  gifts_after: number;
};

export type MemberVoteMoney = {
  bioguide: string;
  full_name: string | null;
  /** Keyed on `vote_id`, and only the rolls with something. Nine rolls in
   *  ten have nothing, and sending them all would be mostly zeroes. */
  votes: Record<string, VoteMoneySummary>;
  coverage: BillLobbyingCoverage;
};

/** One organisation on one roll: what it filed, and what it gave.
 *
 *  `clients` is who it filed on behalf of, which is regularly not itself — a
 *  law firm lobbies for somebody, and the somebody is the interest. */
export type VoteMoneyDonor = {
  registrant_id: number;
  name: string;
  activities: number;
  clients: string[];
  first_year: number | null;
  last_year: number | null;
  amount: number;
  gifts: number;
  before: number;
  after: number;
  unplaced: number;
  gifts_before: number;
  gifts_after: number;
  latest: string | null;
};

export type VoteMoneyDetail = {
  bioguide: string;
  vote_id: string;
  position: string;
  recorded: string | null;
  chamber: string | null;
  roll: number | null;
  date: string | null;
  question: string | null;
  result: string | null;
  bill_id: string | null;
  title: string | null;
  display_name: string | null;
  policy_area: string | null;
  law_number: string | null;
  /** How many organisations disclosed lobbying this bill in total. Most gave
   *  the members voting on it nothing, and the funders listed without this
   *  denominator read as though funding is what lobbying looks like. */
  registrants: number;
  donors: VoteMoneyDonor[];
  /** Funders past the cap. One reconciliation bill has 143 of a single
   *  member's donors on it. */
  hidden: number;
  hidden_amount: number;
  amount: number;
  gifts: number;
  before: number;
  after: number;
  unplaced: number;
  gifts_before: number;
  gifts_after: number;
  coverage: BillLobbyingCoverage;
};

/** One organisation in the index.
 *
 *  `to_members` and `elsewhere` are both FECA money and are deliberately
 *  apart. Ranking on the sum put SoftBank first on a single fifty-million
 *  donation to a president's library and the electrical workers' union
 *  fifth on 1,817 cheques filed against an honoree of "N/A" — real rows,
 *  neither of them money any member of Congress received. */
export type Firm = {
  registrant_id: number;
  name: string;
  to_members: number;
  elsewhere: number;
  gifts: number;
  members: number;
};

export type FirmIndex = { firms: Firm[]; total: number };

export type FirmSort = "money" | "members" | "gifts" | "name";

/** Where the register publishes the sworn document one row came off. Null
 *  only for a row with no filing uuid, and a missing link is better than a
 *  broken one: a 404 reads as an invented figure rather than a missing id. */
export type Sourced = {
  filing_uuid: string | null;
  source: string | null;
};

/** A contribution that went somewhere other than a member's campaign: an
 *  inaugural committee, a presidential library, a conference. */
export type OtherGift = Sourced & {
  registrant_id: number | null;
  registrant: string;
  kind: OtherKind;
  honoree: string;
  payee: string;
  amount: number;
  date: string | null;
  filing_year: number;
};

export type OtherKind = "ple" | "pic" | "me";

export type OtherMoney = {
  summary: {
    kind: OtherKind;
    total: number;
    gifts: number;
    donors: number;
    /** The largest single line in the category, and who it named. $50m of
     *  the $78.9m ever disclosed to presidential libraries is one cheque,
     *  and a category total without that reads as an industry habit rather
     *  than as one filer's decision. */
    biggest: number;
    biggest_honoree: string | null;
  }[];
  /** Every kind against every year, zeros included — never filtered to the
   *  kind being listed. The zeros are the finding: inaugural money is
   *  nothing in 2022, 2023 and 2026 and tens of millions either side of an
   *  inauguration, and a series that omits the empty years draws a line
   *  through them and turns a four-yearly event into a steady stream. */
  by_year: { filing_year: number; kind: OtherKind; total: number; gifts: number }[];
  /** The free-text recipients folded onto the president each one names.
   *  Fifty-eight strings here mention Trump, so as filed this cannot be
   *  charted at all. `recipient` is null for the 7.5% that names no
   *  president — kept as its own bar so the bars still add to the total,
   *  and always last, because a leftover is not a person. */
  by_recipient: {
    recipient: string | null;
    total: number;
    gifts: number;
    parts: { kind: OtherKind; total: number }[];
  }[];
  /** All of it, and what the same forms sent to members of Congress
   *  directly. The second is what gives the first a size: $162m spent
   *  around presidents is enormous or marginal depending on a number this
   *  page never showed, and it is marginal. */
  around_them: number;
  to_members: number;
  gifts: OtherGift[];
  total: number;
};

export type FirmDetail = {
  registrant_id: number;
  name: string;
  to_members: number;
  elsewhere: number;
  honorary: number;
  other: number;
  gifts: number;
  members: number;
  unmatched: number;
  first: string | null;
  last: string | null;
  by_party: Record<string, number>;
  /** `to_members` split by who swore to the filing: "lobbyist" is an
   *  individual reporting their own money with this organisation named as
   *  their employer, "organization" is the organisation itself. Keyed
   *  rather than two fields, so a type the register adds later arrives as a
   *  key instead of being folded into one of these.
   *
   *  Not a nicety. A corporation may not contribute to a federal candidate
   *  at all, so "Apple Inc. gave Josh Riley $15,300" describes a federal
   *  offence, and every one of those cheques was a named employee's own. */
  by_filer: Record<string, { amount: number; gifts: number }>;
  top_members: {
    bioguide: string;
    full_name: string | null;
    party: string | null;
    state: string | null;
    chamber: string | null;
    amount: number;
    gifts: number;
    latest: string | null;
  }[];
  lobbyists: { lobbyist: string; amount: number; gifts: number }[];
  /** `committee_id` when the payee is a party committee with a page of its
   *  own; failing that `bioguide` when every cheque under it resolves to one
   *  member. Both null for the sixth of them that reach neither. */
  payees: {
    payee: string;
    amount: number;
    gifts: number;
    committee_id: string | null;
    bioguide: string | null;
    full_name: string | null;
  }[];
  by_year: { year: number; amount: number; gifts: number }[];
  other_gifts: (Sourced & {
    kind: OtherKind;
    honoree: string;
    payee: string;
    amount: number;
    date: string | null;
    filing_year: number;
  })[];
};

/** One line of one LD-203: a single cheque, as filed.
 *
 *  The rows every total on the site is a sum of, and the thing that was
 *  missing. A chart edge reading "Apple Inc. → Josh Riley, $26,100, 9
 *  cheques" cannot be checked, and two of those nine were one report
 *  counted twice.
 *
 *  `date` is as filed and is not always the day the cheque was written:
 *  filers routinely enter the end of the reporting period instead, so 30
 *  June and 31 December are over-represented. */
export type Cheque = Sourced & {
  contribution_id: string;
  filing_year: number;
  filing_period: string;
  registrant: string;
  registrant_id: number | null;
  lobbyist: string | null;
  /** "lobbyist" or "organization". See `FirmDetail.by_filer`. */
  filer_type: string;
  kind: string;
  payee: string;
  /** As the filer typed it, beside the member it resolved to. */
  honoree: string;
  bioguide: string | null;
  matched_how: string | null;
  amount: number;
  date: string | null;
  full_name?: string | null;
  party?: string | null;
  state?: string | null;
};

export type Cheques = { gifts: Cheque[]; total: number };

/**
 * One person, as named on one organisation's filings.
 *
 * Not "a lobbyist" — a lobbyist at a firm. No federal form issues them a
 * number that survives a change of employer, so the identity here is the
 * pair, and `elsewhere` names the other organisations the same spelling
 * files at without adding a dollar of their money to this page. Whether it
 * is one person is left to the reader, who has the same evidence we do.
 *
 * `filed` has no companion figure splitting it by who swore to it, as an
 * organisation's total does: every row here names this lobbyist, and the
 * register names one only on a filing an individual made. It is still not
 * "what they gave" — the largest such figure in this corpus is $1.8m against
 * one lobbyist at K&L Gates, every line of which names a caucus PAC as its
 * contributor. Never render it without the cheques underneath.
 */
export type Lobbyist = {
  name: string;
  slug: string;
  registrant_id: number;
  registrant: string;
  /** The government job they swore to on an LD-2, verbatim and uncut. */
  position: string | null;
  members: {
    bioguide: string;
    full_name: string | null;
    party: string | null;
    state: string | null;
    chamber: string | null;
  }[];
  first_year: number | null;
  last_year: number | null;
  filed: number;
  gifts: number;
  elsewhere: {
    registrant_id: number;
    registrant: string;
    slug: string;
    gifts: number;
    filed: number;
  }[];
};

/** Ids are namespaced (`firm:32098`, `member:C001088`) so the two kinds of
 *  node cannot collide in one graph. `bioguide` is carried separately
 *  because that, not the node id, is what a member's page is keyed on. */
export type FirmNetwork = {
  anchor: { id: string; kind: "firm"; label: string };
  nodes: {
    id: string;
    kind: "member";
    label: string;
    party: string | null;
    state: string | null;
    bioguide: string;
    amount: number;
    /** The most recent cheque this firm wrote this member. A firm that
     *  stopped giving in 2019 and one still writing looks identical as a
     *  total. */
    latest: string | null;
  }[];
  edges: { source: string; target: string; amount: number; gifts: number }[];
  /** Members who received less and are not drawn. */
  hidden: number;
  /** Everything the firm gave to members, drawn or not — the same figure the
   *  firm's own headline leads with, so a chart can say what share of it it
   *  is showing without contradicting the page around it. */
  to_members: number;
  /** The part of that which went to the members who are not drawn. Nought
   *  when the graph is the whole of it. */
  hidden_amount: number;
};

/** What the LDA calls each of them. Taken from the API's own constants
 *  endpoint rather than guessed, then softened for a reader: "Presidential
 *  Library Expenses" is the filing category, not a thing anyone says. */
export const OTHER_KINDS: Record<OtherKind, { label: string; blurb: string }> = {
  ple: {
    label: "Presidential libraries",
    blurb:
      "Money to a president's library or foundation. There is no ceiling on it, which is why the largest single figure in the whole record is here.",
  },
  pic: {
    label: "Inaugural committees",
    blurb:
      "Money to the committee that stages an inauguration. Also uncapped, and disclosed in the weeks around a handover of power.",
  },
  me: {
    label: "Meetings and retreats",
    blurb:
      "The cost of a conference or a retreat a lobbyist paid for. Not a gift to anyone in particular.",
  },
};

/** What a party committee did with money, about one candidate.
 *
 *  Four values that are never summed. `opposing` is money spent attacking
 *  the candidate it is filed under, so a row carrying it names the person
 *  the money was aimed at rather than the one it helped; adding it to
 *  `coordinated` on the same page would report support that did not happen.
 *
 *  Null for a transaction code the corpus refuses to classify — 24H is an
 *  honorarium paid to the candidate personally and 24R is recount spending,
 *  and neither belongs in any of the four. */
export type Effect = "direct" | "coordinated" | "supporting" | "opposing";

/** The first and last date behind a figure.
 *
 *  Money in and money out come from different sources over different
 *  periods — LD-203 filings one side, FEC cycles the other — so each half
 *  carries its own, and a page that showed one window over both would
 *  invite a comparison neither source supports. */
export type Covers = { first: string | null; last: string | null };

/** One party committee in the index.
 *
 *  `money_in` is everything registered lobbyists disclosed giving it.
 *  `money_in_federal` is the part that could ever reach a candidate: since
 *  2014 a national party committee may also hold headquarters, recount and
 *  convention accounts with far higher limits, and only the federal figure
 *  belongs in the same sentence as `money_out`.
 *
 *  `money_out` is a mapping and deliberately not a total. The key is an
 *  `Effect`, or the string "null" for the unclassified codes, because a
 *  JSON object cannot have a null key. */
export type PartyCommittee = {
  committee_id: string;
  name: string;
  party: string | null;
  money_in: number;
  money_in_federal: number;
  gifts: number;
  transactions: number;
  money_out: Record<string, number>;
};

/** How much party committee spending resolves to a sitting member, per
 *  category as well as overall.
 *
 *  Published per category because one figure across all four would read as
 *  a matching failure rather than as what the money was for: a cheque to a
 *  campaign nearly always lands on somebody who won, and money spent
 *  attacking a candidate nearly never does. */
export type CommitteeCoverage = {
  total: number;
  matched: number;
  dollars: number;
  dollars_matched: number;
  first_cycle: number | null;
  last_cycle: number | null;
  by_effect: {
    effect: Effect | null;
    total: number;
    matched: number;
    dollars: number;
    dollars_matched: number;
  }[];
};

/** The index, and the paging fields it grew.
 *
 *  All four are optional and every one of them is read through a guard.
 *  Asked with no parameters the endpoint still answers with the whole index
 *  and nothing else, which is the shape the four cards and the coverage
 *  block are built on; asked with `page` it answers with a page and says
 *  which. A reader on a build served by an older API must still get a table,
 *  so nothing here may be assumed present. */
export type PartyCommitteeIndex = {
  committees: PartyCommittee[];
  coverage: CommitteeCoverage;
  total?: number;
  page?: number;
  pages?: number;
  per_page?: number;
};

/** One candidate a committee spent money on — or against.
 *
 *  `bioguide` is null for the majority. Most of what a party committee
 *  spends is aimed at challengers and, for the whole of the opposing
 *  category, at the other party's candidates, who by definition did not win
 *  the seat. Those rows are the record being right, so they are shown with
 *  the FEC candidate id rather than dropped. */
export type CommitteeCandidate = {
  candidate_id: string;
  bioguide: string | null;
  full_name: string | null;
  party: string | null;
  state: string | null;
  chamber: string | null;
  amount: number;
  transactions: number;
};

export type CommitteeMoneyIn = {
  /** The only figure that can be held against money out. */
  federal: number;
  covers: Covers | null;
  by_account: { account: string; amount: number; gifts: number; donors: number }[];
  /** Federal account only, since that is the money the page is about. */
  donors: {
    registrant_id: number | null;
    registrant: string;
    amount: number;
    gifts: number;
    latest: string | null;
  }[];
  by_year: { year: number; amount: number; gifts: number }[];
  /** "acronym" or "name" — how the filer's spelling was resolved to this
   *  committee. Not equally certain, so not rendered identically. */
  by_confidence: Record<string, number>;
};

export type CommitteeMoneyOut = {
  by_effect: { effect: Effect | null; amount: number; transactions: number; candidates: number }[];
  by_type: {
    transaction_type: string;
    effect: Effect | null;
    amount: number;
    transactions: number;
  }[];
  by_cycle: { cycle: number; effect: Effect | null; amount: number }[];
  /** Keyed the same way `money_out` is, and four separate lists rather than
   *  one: a candidate a committee gave $1,000 to and spent $50,000 attacking
   *  belongs in two of them and never as $51,000. */
  candidates: Record<string, CommitteeCandidate[]>;
  covers: Covers | null;
};

export type PartyCommitteeDetail = {
  committee_id: string;
  name: string;
  treasurer: string | null;
  state: string | null;
  designation: string | null;
  committee_type: string | null;
  party: string | null;
  connected_org: string | null;
  money_in: CommitteeMoneyIn;
  money_out: CommitteeMoneyOut;
};

/** What one party committee spent on one member, in one category.
 *
 *  A member's largest entry is routinely the other party's committee
 *  spending against them, which is not money they were given in any sense,
 *  so `effect` travels with `amount` everywhere and neither is shown
 *  without the other. */
export type MemberCommitteeMoney = {
  committee_id: string;
  name: string | null;
  committee_party: string | null;
  effect: Effect | null;
  amount: number;
  transactions: number;
};

/** An organisation in its own words, off whichever form has them.
 *
 *  The only sentence about an organisation anywhere in this API that the
 *  organisation wrote. `attribution` and `source` are never null and are
 *  never rendered apart from `text` — a quote with nothing beside it is
 *  indistinguishable from one this site composed, and that distinction is
 *  the whole of why the panel can be believed.
 *
 *  Two forms feed it and `attribution` says which. The LD-1 register entry
 *  is an organisation describing itself, and is preferred. Where that is
 *  blank — a third of everyone who gives money, including the largest
 *  givers in the corpus — it falls back to a quarterly LD-2, which is the
 *  organisation describing three months of its own work. A worse answer to
 *  "who are you" than the first and a far better one than silence, but a
 *  different claim, so it never arrives looking like the first. */
export type SelfDescription = {
  text: string;
  attribution: string;
  source: string;
  /** What to call the link to `source`. Sent rather than chosen by the page
   *  because the two forms are two different documents, and a link reading
   *  "their entry on the register" pointing at one quarter's activity report
   *  would be the one dishonest thing on the panel. */
  link: string;
  /** The filer wrote more than this. Their paragraphs run to two thousand
   *  characters and the tail is always a list of bill numbers, so the quote
   *  stops at a sentence — which the page has to show, or it is telling the
   *  reader the filer said less than they did. */
  trimmed: boolean;
  /** The earliest quarter this same paragraph was filed in, where it came
   *  off an LD-2. Filers copy the previous quarter forward, so it says how
   *  settled the position is. Absent for a register entry. */
  since?: number | null;
  /** The quarter the copy being quoted was filed in. Absent for a register
   *  entry, which has no quarter. */
  year?: number | null;
};

/** A registration fact, with the label that makes it one.
 *
 *  The label travels with the value because it is half the datum:
 *  "Washington, DC" under "Registered from" is a line off a federal form,
 *  and on its own it is decoration. */
export type ProfileFact = { label: string; value: string };

/** One subject an organisation discloses lobbying on, and how much of its
 *  disclosed activity that is.
 *
 *  A share rather than a rank. "Foreign Relations and Defense are three
 *  quarters of what they file" says something about an organisation; "Foreign
 *  Relations is first" is equally true of a firm spread evenly over twenty
 *  codes. */
export type ProfileSubject = {
  code: string;
  label: string;
  activities: number;
  share: number;
};

/** Who pays for the lobbying.
 *
 *  For a registrant these are its clients, and `amount` is null: the LD-2
 *  income figure is filed per client and is not held here, so the honest
 *  ordering is by how much of the firm's disclosed activity each client
 *  accounts for. For a party committee these are the registrants whose
 *  lobbyists certified giving to its federal account, and `amount` is real
 *  money. */
export type ProfileFunder = {
  name: string;
  id: number | null;
  activities?: number;
  amount?: number;
  gifts?: number;
  first?: number | null;
  last?: number | null;
};

/** One named person, and what record names them.
 *
 *  `position` is the government job they swore to holding before they were a
 *  lobbyist, in their own words off the LD-2 — or "Treasurer" for a party
 *  committee, off the FEC's committee master. `members` are the members of
 *  Congress that post names, where the name resolves to somebody in this
 *  corpus; a career pointing at a member who left in 1996 is real and comes
 *  back with an empty list rather than a guess.
 *
 *  **`filed` is not their own money.** It is the total on the LD-203s filed
 *  in that person's name, and what a report in a lobbyist's name contains is
 *  a separate question: the largest of William Kirk's at K&L Gates is $1.8m
 *  and every line of it names a contributor of "CONGRESSIONAL BLACK CAUCUS
 *  PAC" — a committee he filed for, not a cheque he wrote. The field that
 *  would say whose money it was is on the register's own line and is not
 *  parsed into this corpus yet, so no page renders this figure and the panel
 *  names people without one. */
export type ProfilePerson = {
  name: string;
  position: string | null;
  first_year: number | null;
  last_year: number | null;
  filed: number;
  gifts: number;
  members: {
    bioguide: string;
    matched_how: string | null;
    full_name: string | null;
    party: string | null;
    state: string | null;
    chamber: string | null;
  }[];
};

/**
 * Who an organisation is, assembled out of forms that were asked something
 * else.
 *
 * Every other payload this API serves is a disclosure read back. This one is
 * the answer to a question no filing asks, which is why it arrives as layers
 * rather than as prose: what they said about themselves, what they lobby
 * about, who pays them, and who works there. Nothing in it is generated and
 * there is no field here waiting for a model to fill.
 *
 * `refused` is why `said` is empty, when the register held something that was
 * not a description — a registration date, a placeholder, the organisation's
 * own name back again. It is a different fact from an absence and the panel
 * shows it as one, because it says the gap is the register's and not ours.
 *
 * `empty` is true when there is nothing at any layer, which is the common
 * case: most registrants have one filing and no description. It is answered
 * here rather than worked out by the page, so that saying so can be
 * deliberate instead of four empty headings.
 */
export type Profile = {
  kind: "registrant" | "committee";
  id: string;
  name: string;
  said: SelfDescription | null;
  refused: string | null;
  facts: ProfileFact[];
  subjects: ProfileSubject[];
  /** How many disclosed activities the shares are drawn from. */
  activities: number;
  funders: ProfileFunder[];
  funders_total: number;
  /** Every client is the organisation itself — it lobbies for nobody else.
   *  The single most informative thing this record holds about a membership
   *  organisation: nobody pays AIPAC to lobby. */
  self_funded: boolean;
  people: ProfilePerson[];
  people_total: number;
  empty: boolean;
};

export type Page<T> = {
  laws: T[];
  total: number;
  page: number;
  pages: number;
  per_page: number;
  summarised: number;
  note: string;
};

export class ApiError extends Error {
  // Written out rather than declared as a constructor parameter property,
  // which this build refuses: `erasableSyntaxOnly` only permits TypeScript
  // that vanishes when the types are stripped, and that shorthand emits a
  // real assignment.
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }
  const response = await fetch(url);
  if (!response.ok) {
    // The corpus is rebuilt often enough that a refresh in progress is a
    // normal state, and it answers 503 with its own wording. Passing that
    // through beats a generic failure, because it tells the reader to come
    // back rather than that something is broken.
    const detail = await response
      .json()
      .then((body) => body?.detail)
      .catch(() => null);
    throw new ApiError(response.status, detail ?? `Request failed (${response.status}).`);
  }
  return response.json() as Promise<T>;
}

async function post<T>(path: string, params: Record<string, unknown>): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }
  const response = await fetch(url, { method: "POST" });
  if (!response.ok) {
    const detail = await response
      .json()
      .then((body) => body?.detail)
      .catch(() => null);
    throw new ApiError(response.status, detail ?? `Request failed (${response.status}).`);
  }
  return response.json() as Promise<T>;
}

export const api = {
  laws: (params: {
    page?: number;
    per_page?: number;
    congress?: number;
    policy_area?: string;
    query?: string;
    summarised_only?: boolean;
  }) => get<Page<Law>>("/v1/laws", params),

  law: (billId: string) => get<LawDetail>(`/v1/laws/${billId}`),

  /** POST, and no key: the browser cannot hold one. The server meters this
   *  by address instead, and answers 429 with a Retry-After when a caller
   *  has had their share. */
  ask: (billId: string, question: string) =>
    post<Answer>(`/v1/laws/${billId}/ask`, { question }),

  lawVotes: (billId: string) => get<LawVotes>(`/v1/laws/${billId}/votes`),

  landscape: () => get<Landscape>("/v1/law-votes"),

  band: (band: string, params: { page?: number; per_page?: number }) =>
    get<BandPage>(`/v1/law-votes/${band}`, params),

  movingBills: (params: {
    stage?: string;
    policy_area?: string;
    query?: string;
    page?: number;
    per_page?: number;
  }) => get<MovingBills>("/v1/moving-bills", params),

  /** One bill still in progress. The enacted ones have `law` above, which is
   *  a different payload about a different question. */
  bill: (billId: string) => get<BillDetail>(`/v1/bills/${billId}`),

  members: (params: {
    query?: string;
    state?: string;
    party?: string;
    chamber?: string;
    sort?: MemberSort;
    limit?: number;
    offset?: number;
  }) => get<MemberPage>("/v1/members", params),

  member: (bioguide: string) => get<MemberDetail>(`/v1/members/${bioguide}`),

  memberMoney: (bioguide: string) => get<MemberMoney>(`/v1/members/${bioguide}/money`),

  /** The lobbyists who swore they held a post naming this member, and what
   *  they separately certified giving them. Two disclosures that co-occur;
   *  nothing in the filings joins them. */
  memberRevolvingDoor: (bioguide: string, params?: { limit?: number; offset?: number }) =>
    get<MemberDoor>(`/v1/members/${bioguide}/revolving-door`, params),

  /** The column beside the voting record. Paged with the record it sits
   *  beside, or the second page of votes gets the first page's money. */
  memberVoteMoney: (bioguide: string, params?: { limit?: number; offset?: number }) =>
    get<MemberVoteMoney>(`/v1/members/${bioguide}/vote-money`, params),

  voteMoney: (bioguide: string, voteId: string) =>
    get<VoteMoneyDetail>(`/v1/members/${bioguide}/votes/${voteId}/money`),

  firms: (params: { q?: string; sort?: FirmSort; limit?: number; offset?: number }) =>
    get<FirmIndex>("/v1/lobbying/firms", params),

  firm: (registrantId: number) => get<FirmDetail>(`/v1/lobbying/firms/${registrantId}`),

  firmNetwork: (registrantId: number, limit = 40) =>
    get<FirmNetwork>(`/v1/lobbying/firms/${registrantId}/network`, { limit }),

  /** The individual cheques, each naming the filing it came off. `bioguide`
   *  narrows it to one member, which is the question somebody arrives with:
   *  not what this firm gave, but what those nine cheques were. */
  firmGifts: (
    registrantId: number,
    params: { bioguide?: string; q?: string; limit?: number; offset?: number } = {},
  ) =>
    get<Cheques>(`/v1/lobbying/firms/${registrantId}/gifts`, params),

  /** One person at one organisation. See `Lobbyist` on why it is the pair. */
  lobbyist: (registrantId: number, key: string) =>
    get<Lobbyist>(`/v1/lobbying/firms/${registrantId}/lobbyists/${encodeURIComponent(key)}`),

  lobbyistGifts: (registrantId: number, key: string, params: { limit?: number } = {}) =>
    get<Cheques>(
      `/v1/lobbying/firms/${registrantId}/lobbyists/${encodeURIComponent(key)}/gifts`,
      params,
    ),

  /** Inaugurals, libraries and meetings — the money no member received, and
   *  so the money no member total can show. */
  otherMoney: (params: { kind?: OtherKind | ""; limit?: number; offset?: number }) =>
    get<OtherMoney>("/v1/lobbying/other", params),

  /** Every party committee that moved money, which is far more than the four
   *  national ones: state and county parties are in here too. The page picks
   *  the four out for cards of their own; the rest are a table.
   *
   *  Called with no parameters it answers with the whole index, which is
   *  what the cards and the coverage block need. `q`, `sort`, `page` and
   *  `per_page` narrow it for the table, and are safe to send at an API that
   *  does not know them yet — an unknown query parameter is ignored, and the
   *  table checks whether what came back is a page before believing it is
   *  one. */
  partyCommittees: (
    params: { q?: string; sort?: string; page?: number; per_page?: number } = {},
  ) => get<PartyCommitteeIndex>("/v1/party-committees", params),

  partyCommittee: (committeeId: string) =>
    get<PartyCommitteeDetail>(`/v1/party-committees/${committeeId}`),

  /** Who an organisation is, for the panel that answers it. Two endpoints
   *  and one payload shape, because the key spaces differ and the panel does
   *  not: a registrant id is an integer the Senate issues, a committee id is
   *  a string the FEC issues. */
  firmProfile: (registrantId: number, params: { people?: number } = {}) =>
    get<Profile>(`/v1/lobbying/firms/${registrantId}/profile`, params),

  committeeProfile: (committeeId: string) =>
    get<Profile>(`/v1/party-committees/${committeeId}/profile`),

  policyAreas: () =>
    get<{ count: number; data: { policy_area: string; bills: number; laws: number }[] }>(
      "/v1/policy-areas",
    ),
};
